'use client';

import { Heart, Bed, Bath, Car, Maximize2, MapPin, Building2, TrendingUp } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

interface PropertyCardProps {
  property: {
    id: string;
    slug: string;
    title: string;
    buildingName?: string;
    projectName?: string;
    propertyType?: string;
    rentalPriceMonthly?: number | string;
    price?: number | string;
    bedrooms: number;
    bathrooms: number;
    parkingSpaces?: number;
    floorAreaSqm?: number | string;
    floorNumber?: number;
    furnishing?: string;
    primaryImageUrl?: string;
    images?: string | string[];
    city?: string;
    availableUnits?: number;
  };
}

export default function PropertyCardEnhanced({ property }: PropertyCardProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Format price with commas
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Get bedroom label
  const getBedroomLabel = (bedrooms: number) => {
    if (bedrooms === 0) return 'Studio';
    return `${bedrooms}-Bedroom`;
  };

  // Format property type for display
  const formatPropertyType = (type?: string) => {
    if (!type) return 'Property';
    const typeMap: Record<string, string> = {
      'condo': 'Condo',
      'house_and_lot': 'House & Lot',
      'house_&_lot': 'House & Lot',
      'townhouse': 'Townhouse',
      'office_space': 'Office',
      'commercial': 'Commercial',
      'warehouse': 'Warehouse',
      'agricultural': 'Agricultural Lot',
      'land': 'Land',
      'lot': 'Lot',
      'farm': 'Farm',
      'parking': 'Parking',
      'resort': 'Resort',
    };
    return typeMap[type.toLowerCase()] || type;
  };

  // Get furnishing badge color
  const getFurnishingColor = (furnishing?: string) => {
    switch (furnishing?.toLowerCase()) {
      case 'fully furnished':
      case 'fully':
        return 'bg-red-100 text-[#d60500] border-red-200';
      case 'semi furnished':
      case 'semi':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'bare':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Get image URL
  const getImageUrl = () => {
    if (imageError) return '/images/placeholders/bgc-condo.jpg';

    if (property.primaryImageUrl) return property.primaryImageUrl;

    if (property.images && property.images.length > 0) {
      try {
        const imagesArray = typeof property.images === 'string'
          ? JSON.parse(property.images)
          : property.images;
        if (imagesArray.length > 0) return imagesArray[0];
      } catch (e) {
        // Failed to parse
      }
    }

    return '/images/placeholders/bgc-condo.jpg';
  };

  // Safety check: if no slug, don't render invalid links
  if (!property.slug) {
    console.warn('Property missing slug:', property.id, property.title)
    return null
  }

  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-[#d60500] hover:shadow-2xl transition-all duration-300">
      {/* Image Container */}
      <div className="relative h-64 overflow-hidden bg-gray-100">
        <Link href={`/properties/${property.slug}`}>
          <Image
            src={getImageUrl()}
            alt={property.title}
            fill
            className="object-cover group-hover:scale-110 transition-transform duration-500"
            onError={() => setImageError(true)}
          />
        </Link>

        {/* Overlay Badges */}
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between z-10">
          {/* Furnishing Badge */}
          {property.furnishing && (
            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border backdrop-blur-sm ${getFurnishingColor(property.furnishing)}`}>
              {property.furnishing}
            </span>
          )}

          {/* Like Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              setIsLiked(!isLiked);
            }}
            className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-lg"
          >
            <Heart
              className={`w-5 h-5 ${
                isLiked ? 'fill-red-500 text-red-500' : 'text-gray-600'
              } transition-colors`}
            />
          </button>
        </div>

        {/* Available Units Badge */}
        {property.availableUnits && property.availableUnits > 1 && (
          <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-[#d60500] text-white text-xs font-semibold rounded-full flex items-center gap-1.5 shadow-lg">
            <TrendingUp className="w-3.5 h-3.5" />
            {property.availableUnits} Units Available
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Price */}
        <div className="mb-3">
          <div className="text-xl font-bold text-[#d60500] mb-1">
            {formatPrice(Number(property.rentalPriceMonthly || property.price || 0))}<span className="text-xs font-normal text-gray-500">/month</span>
          </div>
        </div>

        {/* Title - Project Name Only (Luxury Positioning) */}
        <Link href={`/properties/${property.slug}`}>
          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1 group-hover:text-[#d60500] transition-colors">
            {property.projectName || property.city || property.title}
          </h3>
        </Link>

        {/* Meta Row - Structured Info (Bedroom • Type • City • Size • Parking) */}
        <div className="text-sm text-gray-600 mb-4 line-clamp-1">
          {getBedroomLabel(property.bedrooms)} {formatPropertyType(property.propertyType)}
          {property.city && ` • ${property.city.replace(/ City$/i, '')}`}
          {property.floorAreaSqm && ` • ${Number(property.floorAreaSqm)} sqm`}
          {property.parkingSpaces && property.parkingSpaces > 0 && ` • ${property.parkingSpaces} Parking`}
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-200 my-4"></div>

        {/* Property Details Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Bedrooms */}
          <div className="flex items-center gap-2 text-sm">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Bed className="w-4 h-4 text-gray-700" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">{getBedroomLabel(property.bedrooms)}</div>
              <div className="text-xs text-gray-500">Bedrooms</div>
            </div>
          </div>

          {/* Bathrooms */}
          <div className="flex items-center gap-2 text-sm">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Bath className="w-4 h-4 text-gray-700" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">{property.bathrooms}</div>
              <div className="text-xs text-gray-500">Bathrooms</div>
            </div>
          </div>

          {/* Floor Area */}
          <div className="flex items-center gap-2 text-sm">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Maximize2 className="w-4 h-4 text-gray-700" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">{Number(property.floorAreaSqm || 0)} m²</div>
              <div className="text-xs text-gray-500">Floor Area</div>
            </div>
          </div>

          {/* Parking */}
          <div className="flex items-center gap-2 text-sm">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Car className="w-4 h-4 text-gray-700" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">{property.parkingSpaces || 0}</div>
              <div className="text-xs text-gray-500">Parking</div>
            </div>
          </div>
        </div>

        {/* Floor Level */}
        {property.floorNumber && (
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg mb-4">
            <Building2 className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700">
              <span className="font-semibold">Floor {property.floorNumber}</span>
            </span>
          </div>
        )}

        {/* CTA Button */}
        <Link href={`/properties/${property.slug}`}>
          <button className="w-full bg-[#d60500] hover:bg-[#b50400] text-white font-semibold py-3 px-4 rounded-xl transition-all flex items-center justify-center gap-2 group/btn">
            View Details
            <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </Link>
      </div>
    </div>
  );
}
